// useProductHandlers.ts
import { useState } from "react";
import { useProducts } from "./useProducts";
import { useCategories } from "./useCategories";
import { useStaff } from "./useStaff";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import { useUser } from "src/shared/context/UserContext";
import { Product } from "src/shared/services/interfacev2";

interface UseProductHandlersProps {
  businessType: string;
}

interface FormData {
  productName: string;
  arabicName: string;
  amount: string;
  categoryId: string;
  type?: string;
  staffList?: string[]; // Array of staff IDs from the form
  image?: File | null;
  ownProduct?: boolean;
}

export const useProductHandlers = ({ businessType }: UseProductHandlersProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showDeletedProducts, setShowDeletedProducts] = useState(false);
  const [page, setPage] = useState(0);
  const { userConfigration } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefaultSize || 10);

  const { categories } = useCategories();
  const { staff } = useStaff();
  const { products, totalRecords, loading, refetchProducts } = useProducts(showDeletedProducts, page, rowsPerPage, searchTerm, categoryFilter);

  // Helper function to convert staff IDs to staff first names as strings
  const getStaffFirstNamesFromIds = (staffIds: string[]): string[] => {
    if (!staffIds || !staff || staffIds.length === 0) return [];
    
    return staffIds
      .map(id => {
        const staffMember = staff.find(s => s._id.$oid === id);
        return staffMember ? staffMember.firstName : null;
      })
      .filter(name => name !== null) as string[];
  };

  // Helper function to convert staff IDs to full names as strings
  // const getStaffFullNamesFromIds = (staffIds: string[]): string[] => {
  //   if (!staffIds || !staff || staffIds.length === 0) return [];
    
  //   return staffIds
  //     .map(id => {
  //       const staffMember = staff.find(s => s._id.$oid === id);
  //       if (!staffMember) return null;
        
  //       const names = [
  //         staffMember.firstName, 
  //         staffMember.middleName, 
  //         staffMember.lastName
  //       ].filter(name => name && name.trim() !== "");
        
  //       return names.join(" ");
  //     })
  //     .filter(name => name !== null) as string[];
  // };

  // ADD PRODUCT ONLY
  const handleAddProduct = async (formData: FormData) => {
    try {
      const payload = new FormData();
      payload.append("productName", formData.productName);
      payload.append("amount", formData.amount);
      payload.append("arabicName", formData.arabicName);
      payload.append("isNonInventoryItem", String(!formData.ownProduct));
      payload.append("categoryId", formData.categoryId);
      
      // Convert staff IDs to staff names (as simple strings)
      const staffNames = getStaffFirstNamesFromIds(formData.staffList || []);
      // OR use full names: const staffNames = getStaffFullNamesFromIds(formData.staffList || []);
      
      // Send staffList as JSON array of strings
      payload.append("staffList", JSON.stringify(staffNames));
      
      if (formData.type) payload.append("type", formData.type);
      if (formData.image) payload.append("image", formData.image);

      await api.post("/app/pos/v2/product", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notifySuccess("Product added successfully");

      setShowAddModal(false);
      refetchProducts();
    } catch (err: any) {
      const message = err?.response?.data?.message || "Something went wrong";
      notifyError(message);
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setPage(0);
  };

  const handleToggleDeleted = () => {
    setShowDeletedProducts((prev) => !prev);
    setPage(0);
    setSearchTerm("");
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handlePageChange = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(0);
  };

  return {
    products,
    categories,
    staff,
    totalRecords,
    loading,
    selectedProduct,
    showAddModal,
    showEditModal,
    showDeleteModal,
    searchTerm,
    categoryFilter,
    showDeletedProducts,
    page,
    rowsPerPage,
    refetchProducts,
    setShowAddModal,
    handleAddProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleCloseModals,
    handleCategoryChange,
    handleToggleDeleted,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
  };
};